// useEmployeeLoginSwitch — the login switch's fetch/toggle/race logic, lifted
// out of EmployeesPage on 2026-08-24 so the onboarding wizard and the drawer
// can both render the same control.
//
// 🔒 Behaviour-preserving move. The four guards below are the contract; the
// 12 page-level races in EmployeesPage.loginswitch.test.tsx are UNCHANGED and
// still pass against this hook, which is the proof the move kept them:
//   1. Every async write-back is scoped to the employee captured at entry.
//   2. Switching employee clears the state FIRST and synchronously.
//   3. A 403 renders NO switch — never an error, never a dead control.
//   4. No optimistic update: on success we refetch.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/utils/api';
import type { EmployeeLoginState } from './employeeLoginState';

export interface LoginSwitchApi {
  loginState: EmployeeLoginState | null;
  loginLoading: boolean;
  loginBusy: boolean;
  loginError: string | null;
  loginForbidden: boolean;
  toggleLogin: (enabled: boolean, opts?: { email?: string; password?: string }) => Promise<void>;
}

export function useEmployeeLoginSwitch(args: {
  employeeId: string | null;
  open: boolean;
  onChanged?: () => void;
}): LoginSwitchApi {
  const { employeeId, open, onChanged } = args;
  // `loginState` is null whenever we have no trustworthy answer for the
  // employee on screen: before the fetch lands, and permanently when the
  // caller is not allowed to see it (both routes are requireCompensationRole
  // — a sales rep gets 403). Null renders NO switch, which is the only safe
  // default: never show a control the caller cannot use, and never show one
  // person's state on another.
  const [loginState, setLoginState] = useState<EmployeeLoginState | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  // Set only on 403 — distinguishes "not allowed" from "still loading".
  const [loginForbidden, setLoginForbidden] = useState(false);
  // The employee this hook is showing RIGHT NOW. Every async write-back is
  // scoped against it: both the open fetch and the toggle's refetch can land
  // after the caller has moved to another person, and a write-back that
  // ignores that renders one person's switch on another person's record.
  const activeIdRef = useRef<string | null>(null);

  const load = useCallback(async (id: string): Promise<EmployeeLoginState> => {
    // Scope the spinner to the employee on screen — a slow response for a
    // person we've already left must not clear the CURRENT person's line.
    if (activeIdRef.current === id) setLoginLoading(true);
    try {
      const res = await api.get(`/employees/${id}/login`);
      // The route answers UNWRAPPED ({ hasLogin, ... }); the `?.data` arm
      // tolerates a wrapped envelope, as the rest of the page's reads use.
      return (res.data?.data ?? res.data) as EmployeeLoginState;
    } finally {
      if (activeIdRef.current === id) setLoginLoading(false);
    }
  }, []);

  // Fetch whenever the control opens, keyed by the employee id, so switching
  // from one person to another REFETCHES instead of showing the previous
  // person's switch. `cancelled` drops a late response from a person we've
  // already navigated away from.
  useEffect(() => {
    if (!open || !employeeId) return;
    const id = employeeId;
    activeIdRef.current = id;
    let cancelled = false;
    // Clear FIRST and synchronously — a stale switch on the wrong person is
    // the worst failure this control can have.
    setLoginState(null);
    setLoginError(null);
    setLoginForbidden(false);
    // A POST still in flight for the PREVIOUS person must not leave this
    // person's switch disabled for reasons that have nothing to do with them.
    setLoginBusy(false);
    setLoginLoading(false);
    load(id)
      .then((s) => {
        if (!cancelled && activeIdRef.current === id) setLoginState(s);
      })
      .catch((err: any) => {
        if (cancelled || activeIdRef.current !== id) return;
        setLoginState(null);
        // 403 = requireCompensationRole rejected this caller. Render nothing
        // rather than an error or a control they cannot use.
        if (err?.response?.status === 403) setLoginForbidden(true);
        else setLoginError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to load login state');
      });
    return () => { cancelled = true; };
  }, [open, employeeId, load]);

  // No optimistic update. On success we refetch the login state AND tell the
  // caller the row changed, so what the user sees is what the server did.
  const toggleLogin = useCallback(async (enabled: boolean, opts?: { email?: string; password?: string }) => {
    // Capture the employee at entry. POST + refetch is two round-trips, and
    // the caller can move to someone else in between — every write-back below
    // is scoped to `id`, or it lands on the wrong person's switch.
    const id = employeeId;
    if (!id) return;
    const stillOnThisEmployee = () => activeIdRef.current === id;
    setLoginBusy(true);
    setLoginError(null);
    try {
      await api.post(`/employees/${id}/login`, {
        enabled,
        ...(opts?.email ? { email: opts.email } : {}),
        ...(enabled && opts?.password ? { password: opts.password } : {}),
      });
      // The row changed regardless of who is on screen now.
      onChanged?.();
      const fresh = await load(id);
      // Caller moved on — this answer describes someone else. Drop it.
      if (!stillOnThisEmployee()) return;
      setLoginState(fresh);
    } catch (err: any) {
      // 422 carries the reason: department may not onboard, no email, password
      // under 8 characters, or no tenant. Show it next to the switch — but only
      // while the switch still belongs to the person the error is about.
      if (!stillOnThisEmployee()) return;
      setLoginError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to update login');
    } finally {
      if (stillOnThisEmployee()) setLoginBusy(false);
    }
  }, [employeeId, load, onChanged]);

  return { loginState, loginLoading, loginBusy, loginError, loginForbidden, toggleLogin };
}
